<script lang="ts">
  import {
    formatNumber,
    getJoodMembershipType,
    getStr,
    globalCurrencyTag,
    isMobileWidth,
    roundToNDigits,
    type JoodMembershipType,
  } from '../../../common/util';

  export let data: any = {};
  export let variant: 'detailed' | 'compact' = 'detailed';
  const isResponsive = isMobileWidth();

  const userPrimeLevel = getJoodMembershipType();

  /**
   * Decide which price tier applies
   * Mirrors ProductTilePriceNewGroup logic (simplified for RMN use)
   */
  function resolvePriceType(product: any): JoodMembershipType {
    const isVip = userPrimeLevel === 'VIPPRIME';

    const isBasic = userPrimeLevel === 'BASICPRIME';

    if (isVip && product?.vipPrimePrice > 0) {
      return 'VIPPRIME';
    }

    if (isBasic && product?.basicPrimePrice > 0) {
      return 'BASICPRIME';
    }

    return 'NOPRIME';
  }

  /**
   * Get actual price based on resolved type
   */
  function getPrice(type: JoodMembershipType, product: any): number {
    switch (type) {
      case 'VIPPRIME':
        if (product?.tacticalVipPromo && product?.tacticalVipPrimePrice > 0) {
          return Number(product.tacticalVipPrimePrice);
        }

        return Number(product?.vipPrimePrice ?? 0);

      case 'BASICPRIME':
        if (product?.tacticalBasicPromo && product?.tacticalBasicPrimePrice > 0) {
          return Number(product.tacticalBasicPrimePrice);
        }

        return Number(product?.basicPrimePrice ?? 0);

      default:
        return Number(
          product?.discountPrice?.value ??
            (product?.price?.value > 0 ? product.price.value : product?.price > 0 ? product.price : 0)
        );
    }
  }

  // ===== reactive state =====
  $: priceType = resolvePriceType(data);

  $: displayPrice = getPrice(priceType, data);

  $: wasPrice = Number(data?.wasPrice ?? data?.price ?? 0);

  $: savedPrice = wasPrice > displayPrice ? wasPrice - displayPrice : 0;

  $: hasSavings = savedPrice > 0;

  $: savedPercent = hasSavings && wasPrice > 0 ? roundToNDigits((savedPrice * 100) / wasPrice, 0) : 0;

  $: formattedDisplayPrice = displayPrice > 0 ? `<strong>${formatNumber(displayPrice)}</strong>` : '';
</script>

{#if displayPrice > 0}
  <div class="price-container {variant}">
    <section class="middle-left">
      <!-- PRICE -->
      <section class="price">
        <div>
          {@html getStr('jood.price.with.currency', [globalCurrencyTag, formattedDisplayPrice])}
          <span class="vat">
            {getStr('extra.addtocart.inc.of.vat')}
          </span>
        </div>

        <!-- SAVINGS (compact) -->
        {#if variant === 'compact' && hasSavings}
          <div class="saving-strike">
            <span class="striked-off">{formatNumber(wasPrice)}</span>
            {#if !isResponsive}
              <strong class="orange-text">
                {@html getStr('extra.cart.saved.amt', [globalCurrencyTag, formatNumber(savedPrice)])}
              </strong>
            {/if}
          </div>
        {/if}
        <!-- SAVINGS (detailed) -->
        {#if variant === 'detailed' && hasSavings}
          <section class="savings">
            <span class="striked-off">{formatNumber(wasPrice)}</span>
            <strong class="orange-text">
              {@html getStr('extra.cart.saved.amt', [globalCurrencyTag, formatNumber(savedPrice)])}
            </strong>
          </section>
        {/if}
      </section>

      <!-- PERCENT TAG (only detailed) -->
      {#if variant === 'detailed' && hasSavings && savedPercent > 0}
        <section class="save-percent-tag">
          {getStr('product.percentageDiscount.off', [savedPercent])}
        </section>
      {/if}
    </section>
  </div>
{/if}

<style lang="scss">
  @import '../styles/colors.new.scss';
  .price-container {
    border-top: 0.4px solid rgba(221, 221, 221, 1);
    padding-top: 10px;
    .middle-left {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: $primary-black;
      .price {
        :global(.price) {
          font-weight: 700;
          font-size: 14px;
          line-height: 16px;
          @include desktop-max-width {
            font-size: 12px;
          }
        }
        :global(.vat) {
          font-weight: 400;
          font-size: 8px;
          @include desktop-max-width {
            font-size: 6px;
          }
        }
        :global(.c_global-currency-sign) {
          font-weight: 700;
          font-size: 14px;
        }

        .saving-strike {
          .striked-off {
            font-weight: 400;
            font-size: 8px;
            line-height: 10px;
            text-decoration: line-through;
          }
          @include desktop-max-width {
            border-left: 0.25px solid $color-gray-border;
            padding-left: 6px;
          }
        }
        .savings {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 8px;
        }
        .orange-text {
          font-weight: 700;
          font-size: 10px;
          color: $functional-orange;
        }
      }
      .save-percent-tag {
        display: inline-block;
        padding: 3px 4px;
        border-radius: 3px;
        color: $primary-white;
        font-weight: 700;
        font-size: 10px;
        line-height: 12px;
        background-color: $functional-orange;
        margin-top: 4px;

        @include responsive {
          font-size: 9px;
          line-height: 10px;
          padding: 2px 5px;
        }
      }
    }
  }
  .price-container.compact {
    border: 0;
    padding-top: 0;
    @include desktop-max-width {
      border-top: 0.25px solid $color-gray-border;
      padding-top: 4px;
    }
    .middle-left {
      width: 100%;
      .price {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }
</style>
